import { useLazyQuery } from "@apollo/client";
import * as Sentry from "@sentry/react";
import { useEffect, useRef, useState } from "react";
import { Config } from "services/configuration/eatSmartConfig";
import { setInitialDishTypes } from "slices/dishesSlice";
import { selectOrder } from "slices/transactionSlice";
import { AppDispatch } from "store";
import { FETCH_DISH_TYPES } from "utils/gqlOperations";
import { logToSentry } from "utils/logger";
import { getRandomNumberInRange } from "utils/math";
import { getMilliSecondsToNextHour } from "utils/time";
import { deepOmit } from "utils/utils";

import { useInterval, useTimeout } from "hooks/timers";
import { Fridge, TransactionState } from "types";
import { LocalFridgeState } from "types/mainTypes";

import { useAppDispatch, useAppSelector } from "./utilsHooks/storeHooks";

interface Props {
	fridgeId: string;
}

export const handleSetDishTypes = (data: Fridge | LocalFridgeState, appDispatch: AppDispatch) => {
	const cleanedData: LocalFridgeState = deepOmit(data, ["__typename"]);
	appDispatch(setInitialDishTypes(cleanedData.dishes?.types));
};

const getInitialRefetchInterval = () => {
	const isProduction = import.meta.env.MODE === "production";
	const overrideVariable = import.meta.env.VITE_REFETCH_NEXT_HOUR_OVERRIDE;

	if (isProduction) {
		return getMilliSecondsToNextHour();
	}
	if (overrideVariable === undefined) {
		return getMilliSecondsToNextHour();
	}
	return Number.parseInt(import.meta.env.VITE_REFETCH_NEXT_HOUR_OVERRIDE);
};

export const useRefetchDishTypesQuery = ({ fridgeId }: Props) => {
	const appDispatch = useAppDispatch();
	const order = useAppSelector(selectOrder);
	const [refetchInterval, setRefetchInterval] = useState<number | null>(
		getInitialRefetchInterval(),
	);
	const [refetchDebounceTimeout, setRefetchDebounceTimeout] = useState<number | null>(null);
	const [isDeferredRefetchPending, setIsDeferredRefetchPending] = useState(false);
	const { initialStateQueryRefetchInterval, initialStateQueryRefetchDebounce } =
		Config.connectionSettings;
	const isMounted = useRef(true);

	const [getDishTypes, { loading }] = useLazyQuery(FETCH_DISH_TYPES, {
		variables: { id: fridgeId },
		fetchPolicy: "network-only",
		refetchWritePolicy: "overwrite",
		onCompleted: ({ fridge }) => {
			handleSetDishTypes(fridge, appDispatch);
		},
	});

	useEffect(() => {
		return () => {
			isMounted.current = false;
			setRefetchInterval(null);
			setRefetchDebounceTimeout(null);
			setIsDeferredRefetchPending(false);
		};
	}, []);

	const handleRefetchDishTypes = async () => {
		if (!isMounted.current) {
			return;
		}

		setIsDeferredRefetchPending(false);
		logToSentry(`Refetching prices: ${fridgeId}`, "info");

		const response = await getDishTypes();
		logToSentry(`Response from price refetch for fridge: ${fridgeId}`, "info");

		if (response?.data) {
			handleSetDishTypes(response.data.fridge, appDispatch);
		} else {
			Sentry.captureException(new Error("Cannot refetch data"));
		}
	};

	// setup periodic refetch interval
	useInterval(() => {
		// Set the interval to the next hour for consistent hourly refetching
		setRefetchInterval(initialStateQueryRefetchInterval);
		// add debounce to the request not to overload the server
		const debounce = getRandomNumberInRange(0, initialStateQueryRefetchDebounce);
		setRefetchDebounceTimeout(debounce);
	}, refetchInterval);

	// setup timeout for debounced refetch
	useTimeout(() => {
		if (!isMounted.current) {
			return;
		}
		if (!order || order.state === TransactionState.Finished) {
			handleRefetchDishTypes();
		} else {
			setIsDeferredRefetchPending(true);
		}
	}, refetchDebounceTimeout);

	return {
		loading,
		handleRefetchDishTypes,
		isDeferredRefetchPending,
	};
};
